import React from 'react';
import { Box, Typography, Button, TextField, Divider, CircularProgress } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import LaunchIcon from '@material-ui/icons/Launch';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import HomeWorkOutlinedIcon from '@material-ui/icons/HomeWorkOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { cuteStarActions } from 'src/store/slices/cuteStarSlice';
import { paymentThunks, paymentActions } from 'src/store/slices/paymentSlice';
import CuteStar from 'src/components/CuteStar/CuteStar';

const DEMO_PRODUCT_NAMES = {
  'ABC123': 'B5WF U78418 WB',
  'DEF456': 'B3WT 71011 XS',
  'GHI789': 'B7WF 96243 MB',
  'SKU001': 'B5WF U78418 WB',
  'SKU002': 'B3WF 74210 WB'
};
const getDemoName = sku => DEMO_PRODUCT_NAMES[sku] || (sku ? 'B5WF U78418 WB' : '—');

const CustomerInfo = (props) => {
  const {
    currency, productLink, onValidityChange,
    customerName, setCustomerName,
    customerSurname, setCustomerSurname,
    customerPhone, setCustomerPhone,
    customerEmail, setCustomerEmail,
    city, setCity,
    street, setStreet,
    postCode, setPostCode,
    addressFree, setAddressFree,
    invalidFields // passed from ProductSales to show errors
  } = props;

  const dispatch = useDispatch();
  const sliceSKU = useSelector(s => s.payment.productSKU);
  const slicePrice = useSelector(s => s.payment.productPrice);
  const sliceWO = useSelector(s => s.payment.workOrderId);
  const sku = sliceSKU;
  const price = slicePrice;
  const workOrderId = sliceWO;
  const productName = getDemoName(sku);
  const starWrapperRef = React.useRef(null);
  const productPanelRef = React.useRef(null); // product card ref
  const STAR_SIZE = 110;

  // WorkOrder verification result
  const verifyData = useSelector(s => s.payment.verifyWorkOrderData);
  const verifiedObject = verifyData?.ResponseObject;
  const appliedRef = React.useRef(false);

  // If WorkOrder verified fill real data once
  React.useEffect(() => {
    if (!workOrderId) return;
    if (!verifiedObject) return;
    if (verifiedObject.WorkOrderId !== Number(workOrderId)) return;
    if (appliedRef.current) return;
    appliedRef.current = true;
    setCustomerName(v => v || verifiedObject.CustomerName || '');
    setCustomerSurname(v => v || verifiedObject.CustomerSurname || '');
    setCustomerPhone(v => v || verifiedObject.CustomerPhone || '');
    setCustomerEmail(v => v || verifiedObject.CustomerEmail || '');
    setCity(v => v || verifiedObject.CustomerCity || '');
    setStreet(v => v || verifiedObject.CustomerStreet || '');
    setPostCode(v => v || verifiedObject.CustomerPostCode || '');
    setAddressFree(v => v || verifiedObject.CustomerAddressText || '');
  }, [
    workOrderId,
    verifiedObject,
    setCustomerName,
    setCustomerSurname,
    setCustomerPhone,
    setCustomerEmail,
    setCity,
    setStreet,
    setPostCode,
    setAddressFree
  ]);

  // CuteStar message
  React.useEffect(() => {
    dispatch(cuteStarActions.setExpression('happy'));
    if (workOrderId && verifiedObject && verifiedObject.WorkOrderId === Number(workOrderId)) {
      dispatch(
        cuteStarActions.setMessage(
          'WorkOrder validated, customer information auto-filled!'
        )
      );
    } else if (workOrderId) {
      dispatch(
        cuteStarActions.setMessage(
          'WorkOrder selected but validation is pending / not found; you can fill the fields manually.'
        )
      );
    } else {
      dispatch(
        cuteStarActions.setMessage(
          'No WorkOrder; you must fill customer name, surname, email and address text.'
        )
      );
    }
    dispatch(cuteStarActions.setShowBubble(true));
  }, [dispatch, workOrderId, verifiedObject]);

  // Validasyon
  React.useEffect(() => {
    if (workOrderId && verifiedObject && verifiedObject.WorkOrderId === Number(workOrderId)) {
      onValidityChange && onValidityChange(true);
      return;
    }
    const ok =
      customerName.trim() &&
      customerSurname.trim() &&
      customerPhone.trim() &&
      customerEmail.trim() &&
      addressFree.trim();
    onValidityChange && onValidityChange(!!ok);
  }, [
    workOrderId,
    verifiedObject,
    customerName,
    customerSurname,
    customerPhone,
    customerEmail,
    addressFree,
    onValidityChange
  ]);

  // Position star slightly to the right of product panel
  React.useLayoutEffect(() => {
    if (!starWrapperRef.current || !productPanelRef.current) return;
    const wrapRect = starWrapperRef.current.getBoundingClientRect();
    const panelRect = productPanelRef.current.getBoundingClientRect();
    // panel soldan uzaklık + panel genişliği - yıldızın bir kısmı (daha “sağında” dursun)
    let left = (panelRect.left - wrapRect.left) + panelRect.width - STAR_SIZE * 2;
    // left = Math.min(Math.max(0, left), wrapRect.width - (STAR_SIZE * 0.25));
    const top = 16; // kartın üstünden biraz aşağı
    dispatch(cuteStarActions.setPosition({ left, top }));
  }, [dispatch, sku, price]); // realign when product changes

  const verifiedCustomerId = useSelector(s => s.payment.verifiedCustomerId);
  const consumerSearchResults = useSelector(s => s.payment.consumerSearchResults);
  const consumerSearchLoading = useSelector(s => s.payment.consumerSearchLoading) === 'Loading';
  const consumerInfoData = useSelector(s => s.payment.consumerInfoData);
  const consumerInfoLoading = useSelector(s => s.payment.consumerInfoLoading) === 'Loading';
  const cuiNumber = useSelector(s => s.payment.cuiNumber);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchFired, setSearchFired] = React.useState(false);
  const [selectedConsumerId, setSelectedConsumerId] = React.useState(null);

  // Debounce search
  React.useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed.length <= 3) {
      setSearchFired(false);
      return;
    }
    const h = setTimeout(() => {
      dispatch(paymentThunks.SearchConsumer(trimmed));
      setSearchFired(true);
    }, 350);
    return () => clearTimeout(h);
  }, [searchTerm, dispatch]);

  const applyConsumer = (c) => {
    if (!c) return;
    setSelectedConsumerId(c.Id);
    // Fetch full details by id (search returns only Id,Name,Surname)
    dispatch(paymentThunks.GetConsumerInfo(c.Id));
  };

  // Fill form when consumer info fetched and lock inputs; set verifiedCustomerId
  React.useEffect(() => {
    const resp = consumerInfoData?.ResponseObject;
    if (!resp || !selectedConsumerId || resp.Id !== selectedConsumerId) return;
    setCustomerName(resp.Name || '');
    setCustomerSurname(resp.Surname || '');
    setCustomerPhone(resp.CustomerPhone || '');
    setCustomerEmail(resp.CustomerEmail || '');
    setCity(resp.Address?.City || '');
    setStreet(resp.Address?.Street || '');
    setPostCode(resp.Address?.PostCode || '');
    setAddressFree(resp.Address?.Address || '');
    dispatch(paymentActions.setVerifiedCustomerId(resp.Id));
    onValidityChange && onValidityChange(true);
  }, [
    consumerInfoData, selectedConsumerId,
    setCustomerName, setCustomerSurname, setCustomerPhone, setCustomerEmail,
    setCity, setStreet, setPostCode, setAddressFree, dispatch, onValidityChange
  ]);

  const clearSelection = () => {
    setSelectedConsumerId(null);
    dispatch(paymentActions.clearConsumerSearch());
    dispatch(paymentActions.clearConsumerInfo());
    dispatch(paymentActions.setVerifiedCustomerId(null));
    dispatch(paymentActions.setCuiNumber(''));
    setSearchTerm('');
    // New: reset customer fields
    setCustomerName('');
    setCustomerSurname('');
    setCustomerPhone('');
    setCustomerEmail('');
    setCity('');
    setStreet('');
    setPostCode('');
    setAddressFree('');
    // Optionally update validity
    onValidityChange && onValidityChange(false);
  };

  const isInvalid = (key) => Array.isArray(invalidFields) && invalidFields.includes(key);

  // Validate locally (email optional)
  const validateCustomerFields = () => {
    const invalid = [];
    if (!customerName.trim()) invalid.push('Name');
    if (!customerSurname.trim()) invalid.push('Surname');
    if (!customerPhone.trim()) invalid.push('CustomerPhone');
    if (!city.trim()) invalid.push('City');
    if (!street.trim()) invalid.push('Street');
    if (!postCode.trim()) invalid.push('PostCode');
    if (!addressFree.trim()) invalid.push('Address');
    // propagate invalid list up if needed
    if (props.setInvalidFields) props.setInvalidFields(invalid);
    // also affect validity state
    onValidityChange && onValidityChange(invalid.length === 0);
    return invalid.length === 0;
  };

  // Create customer action
  const handleCreateCustomer = () => {
    if (verifiedCustomerId || workOrderId) return; // disabled case
    if (!validateCustomerFields()) return;
    const dto = {
      Id: 0,
      Name: customerName.trim(),
      Surname: customerSurname.trim(),
      CustomerPhone: customerPhone.trim(),
      CustomerEmail: (customerEmail || '').trim(),
      CuiNumber: (cuiNumber || '').trim(),
      Address: {
        Id: 0,
        City: city.trim(),
        Street: street.trim(),
        PostCode: postCode.trim(),
        Address: addressFree.trim()
      }
    };
    dispatch(paymentThunks.CreateCustomer(dto))
      .then(res => {
        const newId = res?.payload?.ResponseObject?.Id ?? res?.payload?.Id;
        if (newId) {
          dispatch(paymentActions.setVerifiedCustomerId(newId));
          onValidityChange && onValidityChange(true);
        }
      })
      .catch(() => {});
  };

  // Disable inputs if a consumer has been selected or work order verified
  const disabledAll = !!verifiedCustomerId || !!(workOrderId && verifiedObject && verifiedObject.WorkOrderId === Number(workOrderId));

  return (
    <Box mt={3}>
      <div ref={starWrapperRef} className="force-visible-star" style={{ position:'relative', width:'100%', minHeight:160, margin:0, overflow:'visible' }}>
        <style>{`.force-visible-star .cute-star-mascot{visibility:visible!important;opacity:1!important;}@media(max-width:600px){.force-visible-star{display:none!important;}}`}</style>
        <CuteStar size={STAR_SIZE} floating />
      
        {/* Modern product summary panel */}
        <Box
          style={{
            position: 'relative',
            padding: '18px 16px 16px',
            borderRadius: 22,
            background: 'linear-gradient(145deg,rgba(250,252,255,.85),rgba(235,244,255,.75))',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            border: '1px solid rgba(160,190,220,.55)',
            boxShadow: '0 4px 10px -2px rgba(25,55,110,.15), 0 2px 3px -1px rgba(25,55,110,.08)',
            overflow: 'hidden',
            marginTop: 8,
            transition: 'box-shadow .35s ease, transform .35s ease'
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow='0 8px 26px -6px rgba(25,55,110,.28)'; e.currentTarget.style.transform='translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow='0 4px 10px -2px rgba(25,55,110,.15), 0 2px 3px -1px rgba(25,55,110,.08)'; e.currentTarget.style.transform='none'; }}
        >
          <div style={{
            position:'absolute',
            inset:0,
            background:'radial-gradient(circle at 88% 18%,rgba(120,170,255,.35),rgba(120,170,255,0) 65%)'
          }} />
          <div style={{
            position:'absolute',
            top:0,left:0,right:0,
            height:5,
            background:'linear-gradient(90deg,#3b82f6,#60a5fa,#93c5fd)',
            opacity:.9
          }} />
          <Box display="flex" alignItems="center" mb={1}>
            <InfoOutlinedIcon style={{ fontSize:18, color:'#3167b8', marginRight:6 }} />
            <Typography
              variant="subtitle2"
              style={{
                fontWeight:700,
                fontSize:12.5,
                letterSpacing:'.65px',
                textTransform:'uppercase',
                color:'#2c5c9e'
              }}
            >Selected Product</Typography>
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            gridGap={8}
            style={{ marginBottom: 12 }}
          >
            {/* Single chip: SKU – ProductName */}
            <span style={chipStyle}>
              {sku ? `${sku} – ${productName}` : 'No Product Selected'}
            </span>
            <span style={chipStyle}>
              Price: {price ? `${price} ${currency}` : '—'}
            </span>
            {workOrderId && (
              <span style={chipStyle}>
                WorkOrder: {workOrderId}
              </span>
            )}
          </Box>

          {/* ...existing buttons block... */}
          <Box display="flex" alignItems="center" gridGap={12}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={!price}
              onClick={() => window.open(productLink, '_blank', 'noopener')}
              startIcon={<LinkIcon style={{ fontSize: 16 }} />}
              style={{ textTransform: 'none', fontSize: 12, fontWeight: 600 }}
            >
              Product Link
            </Button>
          </Box>
        </Box>
      </div> {/* FIX: close the wrapper div */}

      {/* Consumer Search (hidden if WorkOrder exists) */}
      {!workOrderId && (
        <Box mb={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 6 }}>
            Search Existing Customer
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Type at least 4 characters (Name)"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {consumerSearchLoading && (
            <Box mt={1} display="flex" alignItems="center" gridGap={8}>
              <CircularProgress size={18} />
              <Typography variant="caption">Searching...</Typography>
            </Box>
          )}
          {searchFired && !consumerSearchLoading && consumerSearchResults?.length === 0 && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 6 }}>
              No customers found.
            </Typography>
          )}
          {consumerSearchResults?.length > 0 && (
            <Box mt={1} style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e0e7ef', borderRadius: 6, padding: 6 }}>
              {consumerSearchResults.map(c => {
                const active = selectedConsumerId === c.Id;
                return (
                  <Box
                    key={c.Id}
                    onClick={() => applyConsumer(c)}
                    style={{
                      padding: '6px 8px',
                      marginBottom: 4,
                      cursor: 'pointer',
                      borderRadius: 4,
                      background: active ? '#1976d2' : '#f7f9fc',
                      color: active ? '#fff' : '#123',
                      border: active ? '1px solid #1976d2' : '1px solid #d6e1eb',
                      fontSize: 12,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <strong style={{ fontSize: 12 }}>{c.Name} {c.Surname}</strong>
                    {/* details load status */}
                    {active && consumerInfoLoading && (
                      <span style={{ fontSize: 11, opacity: .8 }}>Loading details...</span>
                    )}
                  </Box>
                );
              })}
              <Box mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearSelection}
                  disabled={!selectedConsumerId && !(consumerSearchResults?.length)}
                  style={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* -------- Customer Info Form -------- */}
      <Box mt={3} display="flex" flexDirection="column" gridGap={24}>
        {/* Customer section */}
        <Box style={{ background:'#ffffff', border:'1px solid #e3eaf1', borderRadius:16, padding:'16px 18px', boxShadow:'0 2px 8px -2px rgba(15,40,70,.10)' }}>
          <Typography variant="subtitle2" style={{ fontWeight:700, fontSize:12, letterSpacing:'.55px', textTransform:'uppercase', color:'#24507d', marginBottom:12 }}>
            Customer
          </Typography>
          <Box display="flex" flexWrap="wrap" gridGap={14}>
            <TextField label="Customer Name" variant="outlined" size="small" disabled={disabledAll} value={customerName} onChange={e => setCustomerName(e.target.value)} error={isInvalid('Name')} helperText={isInvalid('Name') ? 'Required' : ''} style={{ flex:'1 1 180px', minWidth:140 }} />
            <TextField label="Customer Surname" variant="outlined" size="small" disabled={disabledAll} value={customerSurname} onChange={e => setCustomerSurname(e.target.value)} error={isInvalid('Surname')} helperText={isInvalid('Surname') ? 'Required' : ''} style={{ flex:'1 1 180px', minWidth:140 }} />
            <TextField label="Customer Phone" variant="outlined" size="small" disabled={disabledAll} value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} error={isInvalid('CustomerPhone')} helperText={isInvalid('CustomerPhone') ? 'Required' : ''} style={{ flex:'1 1 160px', minWidth:130 }} />
            <TextField label="Customer Email" variant="outlined" size="small" type="email" disabled={disabledAll} value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} style={{ flex:'1 1 200px', minWidth:160 }} />
            <TextField label="CUI Number" variant="outlined" size="small" disabled={disabledAll} value={cuiNumber} onChange={e => dispatch(paymentActions.setCuiNumber(e.target.value))} style={{ flex:'1 1 140px', minWidth:120 }} />
          </Box>
        </Box>

        <Divider style={{ opacity:.4 }} />

        {/* Address section */}
        <Box style={{ background:'#ffffff', border:'1px solid #e3eaf1', borderRadius:16, padding:'16px 18px', boxShadow:'0 2px 8px -2px rgba(15,40,70,.08)' }}>
          <Typography variant="subtitle2" style={{ fontWeight:700, fontSize:12, letterSpacing:'.55px', textTransform:'uppercase', color:'#24507d', marginBottom:12 }}>
            Address
          </Typography>
          <Box display="flex" flexWrap="wrap" gridGap={14}>
            <TextField label="City" variant="outlined" size="small" disabled={disabledAll} value={city} onChange={e => setCity(e.target.value)} error={isInvalid('City')} helperText={isInvalid('City') ? 'Required' : ''} style={{ minWidth:180, flex:1 }} />
            <TextField label="Street" variant="outlined" size="small" disabled={disabledAll} value={street} onChange={e => setStreet(e.target.value)} error={isInvalid('Street')} helperText={isInvalid('Street') ? 'Required' : ''} style={{ minWidth:200, flex:1 }} />
            <TextField label="Post Code" variant="outlined" size="small" disabled={disabledAll} value={postCode} onChange={e => setPostCode(e.target.value)} error={isInvalid('PostCode')} helperText={isInvalid('PostCode') ? 'Required' : ''} style={{ minWidth:140, flex:1 }} />
          </Box>
          <TextField label="Address (Free Text)" variant="outlined" multiline minRows={4} disabled={disabledAll} value={addressFree} onChange={e => setAddressFree(e.target.value)} error={isInvalid('Address')} helperText={isInvalid('Address') ? 'Required' : ''} placeholder="Detailed address information..." fullWidth style={{ marginTop:16 }} />
        </Box>

        {/* Create Customer button (hidden/disabled based on selection/workorder) */}
        <Box mt={2} display="flex" justifyContent="center">
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={handleCreateCustomer}
            disabled={!!verifiedCustomerId || !!workOrderId}
            style={{ textTransform: 'none' }}
          >
            Create Customer
          </Button>
        </Box>
      </Box>
      {/* ...existing code... */}
    </Box>
  );
};

const chipStyle = {
  fontSize:12,
  fontWeight:600,
  padding:'6px 12px',
  background:'linear-gradient(135deg,#ffffff,#f4f8fc)',
  border:'1px solid #c9d8e6',
  borderRadius:14,
  color:'#0f3254',
  boxShadow:'0 1px 2px rgba(0,0,0,.06)'
};
export default CustomerInfo;
